import { jsPDF } from 'jspdf';

import { stampFamlaBrandingOnJsPdf } from './exportBranding';

type GuideSection = {
    name?: string;
    objective?: string;
    order?: number;
    questions?: Array<{ text?: string; status?: string; order?: number }>;
};

function sortNum(a?: number, b?: number): number {
    return (a ?? 0) - (b ?? 0);
}

function sanitizeFilename(s: string): string {
    return (s || 'guide').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 80);
}

/**
 * Exporte le guide (questions conservées) en PDF côté client.
 */
export function downloadCollectGuidePdf(opts: {
    collectionName: string;
    sections: GuideSection[];
    strings: {
        documentTitle: string;
        approvedStamp?: string;
        sectionObjective?: string;
        questionPrefix: string;
    };
}): void {
    const { collectionName, sections, strings } = opts;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    const maxW = pageW - margin * 2;
    let y = margin;

    const lineHeight = 14;
    const sectionGap = 10;

    const ensureSpace = (extra: number) => {
        if (y + extra > pageH - margin) {
            doc.addPage();
            y = margin;
        }
    };

    const writeParagraph = (text: string, fontSize: number, gapAfter = sectionGap) => {
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(text.trim(), maxW);
        ensureSpace(lines.length * lineHeight + gapAfter);
        doc.text(lines, margin, y);
        y += lines.length * lineHeight + gapAfter;
    };

    doc.setFontSize(18);
    writeParagraph(strings.documentTitle, 18, 12);
    doc.setFontSize(11);
    writeParagraph(collectionName.trim() || '—', 11, 8);
    if (strings.approvedStamp) {
        doc.setFontSize(10);
        writeParagraph(strings.approvedStamp, 10, 14);
    }

    const sorted = [...sections].sort((a, b) => sortNum(a.order, b.order));
    let qGlobal = 0;
    for (const sec of sorted) {
        const kept = (sec.questions || [])
            .filter((q) => String(q.status || '').toLowerCase() === 'kept')
            .sort((a, b) => sortNum(a.order, b.order));
        if (!kept.length) continue;

        doc.setFontSize(13);
        writeParagraph(sec.name?.trim() || '—', 13, 6);
        const obj = (sec.objective || '').trim();
        if (obj && strings.sectionObjective) {
            doc.setFontSize(10);
            writeParagraph(`${strings.sectionObjective}: ${obj}`, 10, 8);
        }

        for (const q of kept) {
            qGlobal += 1;
            const qt = (q.text || '').trim();
            if (!qt) continue;
            doc.setFontSize(11);
            writeParagraph(`${strings.questionPrefix} ${qGlobal}: ${qt}`, 11, 6);
        }
        y += 4;
    }

    stampFamlaBrandingOnJsPdf(doc, collectionName);
    doc.save(`${sanitizeFilename(collectionName)}-guide.pdf`);
}
